import React, { useEffect, useState } from "react";
import useSWR, { mutate } from "swr";

import ProxyBid from "./buyingOptions/ProxyBid";
import ProxyBidSkeleton from "./buyingOptions/ProxyBidSkeleton";
import ErrorState from "./ErrorState";
import placeProxyBid from "../api/placeProxyBid";
import fetchBidInfo from "../api/fetchBidInfo";
import useAsync from "../hooks/useAsync.hook";
import UserBidRights from "./buyingOptions/UserBidRights";
import acceptTermsAndConditions from "./buyingOptions/acceptTermsAndConditions";
import ErrorPlaceProxyBid from "./ErrorPlaceProxyBid";
import { useAuth } from "@bca-auth0/bca-auth0-provider";

export interface ProxyBidWithFetchProps {
  biddingInfoUrl: string;
  placeBidUrl: string;
  acceptTermsAndConditionsUrl: string;
  isTracked: boolean;
  vrm: string | string[];
  setIsTracked: (value: boolean) => void;
  significantFields: any;
  saleStartDate: string;
  inventoryId: string | null;
  isToUseAuthZero?: boolean;
}

interface TermsAndConditions {
  termsAndConditionsNeedAccepting: boolean;
  termsAndConditionsLink: string;
  links: {
    rel: string;
    href: string;
  }[];
  degradedServices: string[];
}

interface ProxyBidInfo {
  userCanPlaceBid: boolean;
  biddingViewType: "ProxyBid";
  userBidStatus: string;
  bidIncrement: number;
  currencyCode: string;
  saleStartDate: string;
  proxyBid: null | {
    amount: number;
    currencyCode: string;
  };
}

const getExpiryDate = (saleStartDate: string): string => {
  const processedSaleStartDate = saleStartDate?.split("T")[0];
  const expiryHour = "23:59:59";

  return `${processedSaleStartDate}T${expiryHour}Z`;
};

const ProxyBidWithFetch: React.FunctionComponent<ProxyBidWithFetchProps> = ({
  biddingInfoUrl,
  placeBidUrl,
  acceptTermsAndConditionsUrl,
  isTracked,
  vrm,
  setIsTracked,
  significantFields,
  saleStartDate,
  inventoryId,
  isToUseAuthZero = false,
}) => {
  const { accessToken } = useAuth(!isToUseAuthZero);
  const expiryDate = getExpiryDate(saleStartDate);
  const [bidAmount, setBidAmount] = useState(0);
  const [termsAndConditionsNeedAccepting, setTermsAndConditionsNeedAccepting] =
    useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [deleteBidStatus, setDeleteBidStatus] = useState("idle");

  const { data: bidInfo, error: bidInfoError } = useSWR<ProxyBidInfo>(
    (biddingInfoUrl && !isToUseAuthZero) ||
      (biddingInfoUrl && isToUseAuthZero && accessToken)
      ? [biddingInfoUrl, isToUseAuthZero, accessToken]
      : null,
    fetchBidInfo,
    {
      shouldRetryOnError: false,
    }
  );

  const { data: termsAndConditions, error: termsAndConditionsError } =
    useSWR<TermsAndConditions>(
      (acceptTermsAndConditionsUrl && !isToUseAuthZero) ||
        (acceptTermsAndConditionsUrl && isToUseAuthZero && accessToken)
        ? [acceptTermsAndConditionsUrl, "get", isToUseAuthZero, accessToken]
        : null,
      acceptTermsAndConditions,
      { shouldRetryOnError: false }
    );

  const { execute: placeBid, status: placeBidStatus } = useAsync(
    placeProxyBid(bidAmount, placeBidUrl, significantFields, expiryDate, isToUseAuthZero, accessToken),
    false
  );

  const {
    execute: acceptTC,
    value: acceptTermsAndConditionsResponse,
    status: acceptTermsAndConditionsStatus,
  } = useAsync(
    () => acceptTermsAndConditions(acceptTermsAndConditionsUrl, "put", isToUseAuthZero, accessToken),
    false
  );

  useEffect(() => {
    if (placeBidStatus === "success" || deleteBidStatus === "success") {
      mutate([biddingInfoUrl, isToUseAuthZero, accessToken]);
    }
  }, [biddingInfoUrl, placeBidStatus, deleteBidStatus, isToUseAuthZero, accessToken]);

  useEffect(() => {
    setShowBanner(
      placeBidStatus === "error" ||
        acceptTermsAndConditionsStatus === "error" ||
        deleteBidStatus === "error"
    );
  }, [placeBidStatus, acceptTermsAndConditionsStatus, deleteBidStatus]);

  useEffect(() => {
    // when no bid has been placed avoid updates
    if (bidInfo?.proxyBid) {
      setBidAmount(bidInfo?.proxyBid?.amount);
    }
  }, [bidInfo?.proxyBid?.amount]);

  useEffect(() => {
    if (!acceptTermsAndConditionsResponse) {
      return;
    }

    setTermsAndConditionsNeedAccepting(
      !acceptTermsAndConditionsResponse.success
    );
  }, [acceptTermsAndConditionsResponse]);

  useEffect(() => {
    if (!termsAndConditions) {
      return;
    }

    setTermsAndConditionsNeedAccepting(
      termsAndConditions.termsAndConditionsNeedAccepting
    );
  }, [termsAndConditions]);

  const showErrorsCard = !!bidInfoError || !!termsAndConditionsError;

  return (
    <>
      {!bidInfo && !showErrorsCard && <ProxyBidSkeleton />}
      {bidInfo && !showErrorsCard && (
        <UserBidRights
          errorMessageText="Proxy Bid is only available to Trade Buyers that are registered to buy online"
          userCanPlaceBid={bidInfo.userCanPlaceBid}
        >
          <ProxyBid
            increment={bidInfo.bidIncrement}
            bidAmount={bidAmount}
            existingBid={!!bidInfo?.proxyBid?.amount}
            onPlaceBid={placeBid}
            setDeleteBidStatus={setDeleteBidStatus}
            setBidAmount={setBidAmount}
            termsAndConditionsNeedAccepting={termsAndConditionsNeedAccepting}
            termsAndConditionsLink={termsAndConditions?.termsAndConditionsLink}
            acceptTermsAndConditions={acceptTC}
            isTracked={isTracked}
            vrm={vrm}
            setIsTracked={setIsTracked}
            inventoryId={inventoryId}
            isToUseAuthZero={isToUseAuthZero}
            accessToken={accessToken}
          ></ProxyBid>
        </UserBidRights>
      )}
      {showErrorsCard && (
        <ErrorState
          icon="exclamation-triangle"
          text="Proxy Bid not available at the moment"
          iconWeight="solid"
        />
      )}
      {showBanner && (
        <ErrorPlaceProxyBid closeBanner={() => setShowBanner(false)} />
      )}
    </>
  );
};

export default ProxyBidWithFetch;
